import { CssBaseline, LinearProgress } from '@mui/material'
import i18next from 'i18next'
import React, { Suspense, lazy, useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { ThemeProvider } from './theme/ThemeContext'

import SimpleErrorFallback from './components/ErrorBoundary/SimpleErrorFallback'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import ScrollToTop from './components/ScrollToTop'
import Routing from './routes/routing'
import DefaultSEO from './components/SEO/DefaultSEO'

import en from './locales/en.json'
import vi from './locales/vi.json'

import './App.css'

const SpeedInsights = lazy(() =>
  import('@vercel/speed-insights/react').then((m) => ({
    default: m.SpeedInsights,
  }))
)
const Analytics = lazy(() =>
  import('@vercel/analytics/react').then((m) => ({ default: m.Analytics }))
)
const ScrollProgressBar = lazy(
  () => import('./components/ScrollProgressBar')
)

const initializeI18n = () => {
  const locale = localStorage.getItem('locale')
  i18next.init({
    interpolation: { escapeValue: false },
    lng: locale ?? 'en',
    resources: {
      en: { translation: en },
      vi: { translation: vi },
    },
  })
  return i18next
}

const DeferredVitals = () => {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const enable = () => setReady(true)
    const ric = (window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => number
      cancelIdleCallback?: (id: number) => void
    }).requestIdleCallback

    if (ric) {
      const id = ric(enable, { timeout: 4000 })
      return () =>
        (
          window as Window & { cancelIdleCallback?: (id: number) => void }
        ).cancelIdleCallback?.(id)
    }
    const timer = window.setTimeout(enable, 2500)
    return () => window.clearTimeout(timer)
  }, [])

  if (!ready) return null

  return (
    <Suspense fallback={null}>
      <SpeedInsights />
      <Analytics />
    </Suspense>
  )
}

const App = (): JSX.Element => {
  const i18n = initializeI18n()

  return (
    <React.StrictMode>
      <HelmetProvider>
        <ThemeProvider>
          <CssBaseline />
          <I18nextProvider i18n={i18n}>
            <BrowserRouter>
              <ErrorBoundary fallback={SimpleErrorFallback}>
                <DefaultSEO />
                <Suspense fallback={<LinearProgress />}>
                  <ScrollToTop />
                  <Suspense fallback={null}>
                    <ScrollProgressBar />
                  </Suspense>
                  <Layout>
                    <Routing />
                  </Layout>
                  <DeferredVitals />
                </Suspense>
              </ErrorBoundary>
            </BrowserRouter>
          </I18nextProvider>
        </ThemeProvider>
      </HelmetProvider>
    </React.StrictMode>
  )
}

export default App
